import * as pc from 'playcanvas'
import { GsplatEffectIntroReveal } from './effect-intro-reveal.js'

const DURATION_MS = 2500
const CAMERA_DURATION_MS = 2300
const INTRO_ORBIT_ANGLE = 8
const HANDOFF_SETTLE_MS = 80
const HANDOFF_FADE_MS = 100
const worldToLocalMatrix = new pc.Mat4()

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value))
}

function applyOrbitMotion(focus, baseVector, angle) {
  const radians = -angle * pc.math.DEG_TO_RAD
  const cos = Math.cos(radians)
  const sin = Math.sin(radians)
  return focus.clone().add(new pc.Vec3(
    baseVector.x * cos + baseVector.z * sin,
    baseVector.y,
    -baseVector.x * sin + baseVector.z * cos
  ))
}

function gridAxisCount(extent, maxExtent, baseCount, minCount, maxCount) {
  return clamp(Math.round(extent / Math.max(maxExtent, 0.001) * baseCount), minCount, maxCount)
}

function getLocalEffectCenter(entity, focus, fallback) {
  const center = focus?.clone?.() || fallback?.clone?.() || new pc.Vec3()
  const transform = entity?.getWorldTransform?.()
  if (!transform) return center
  return worldToLocalMatrix.copy(transform).invert().transformPoint(center, new pc.Vec3())
}

function getSplatPointCount(entity) {
  const resource = entity?.gsplat?._assetReference?.asset?.resource
  const count = resource?.gsplatData?.numSplats
    ?? resource?.numSplats
    ?? entity?.gsplat?.instance?.numSplats
    ?? entity?.gsplat?.instance?.resource?.numSplats
    ?? 0
  return Number.isFinite(count) && count > 0 ? count : 0
}

function getPointCountFactor(entity) {
  const pointCount = getSplatPointCount(entity)
  if (!pointCount) return 0.45
  return clamp((Math.log10(pointCount) - 4.75) / 1.45, 0, 1)
}

function createIntroReveal(entity, bounds, focus, cameraEntity) {
  if (!entity || !bounds?.halfExtents) return null
  if (!entity.script) entity.addComponent('script')
  entity.script.destroy(GsplatEffectIntroReveal.scriptName)
  const script = entity.script.create(GsplatEffectIntroReveal)
  if (!script) return null

  const halfExtents = bounds.halfExtents
  const maxExtent = Math.max(halfExtents.x, halfExtents.y, halfExtents.z, 0.001)
  const minExtent = Math.max(Math.min(halfExtents.x, halfExtents.y, halfExtents.z), 0.001)
  const radius = Math.max(halfExtents.length(), 0.001)
  const effectCenter = getLocalEffectCenter(entity, focus, bounds.center)
  const boundsCenter = bounds.center || effectCenter
  const centerOffset = effectCenter.clone().sub(boundsCenter)
  const coverageRadius = Math.hypot(
    halfExtents.x + Math.abs(centerOffset.x),
    halfExtents.y + Math.abs(centerOffset.y),
    halfExtents.z + Math.abs(centerOffset.z)
  )
  const coverageBase = clamp(Math.round(5 + radius * 0.02), 5, 7)
  const detailBase = clamp(coverageBase * 2 - 1, 9, 13)

  // The radial restore starts at the saved camera focus, just like 3dgs_web.
  // The current viewer keeps the model entity untransformed, while the
  // world-to-local conversion also keeps this correct if that later changes.
  script.boundsCenter.copy(effectCenter)
  script.boundsHalfExtents.copy(halfExtents)
  script.coverageGrid.set(
    gridAxisCount(halfExtents.x, maxExtent, coverageBase, 3, 7),
    gridAxisCount(halfExtents.y, maxExtent, coverageBase, 3, 7),
    gridAxisCount(halfExtents.z, maxExtent, coverageBase, 3, 7)
  )
  script.detailGrid.set(
    gridAxisCount(halfExtents.x, maxExtent, detailBase, 5, 13),
    gridAxisCount(halfExtents.y, maxExtent, detailBase, 5, 13),
    gridAxisCount(halfExtents.z, maxExtent, detailBase, 5, 13)
  )
  script.thresholdFeather = 0.04
  script.initialDensity = clamp(0.032 + minExtent * 0.0025, 0.034, 0.06)
  script.densityExponent = clamp(2.8 + radius * 0.01, 2.8, 3.2)
  script.introAppearRatio = 0.18
  script.introAppearExponent = 0.68
  script.particleStartSize = clamp(0.0015 + minExtent * 0.0002, 0.0015, 0.003)
  script.particleGrowExponent = clamp(1.1 + radius * 0.01, 1.1, 1.45)
  script.shapeRestoreStart = 0.58
  script.shapeRestoreEnd = 0.86
  script.rippleStrength = 0
  script.rippleFrequency = 1.35
  script.rippleWidth = 0.16
  script.rippleTravel = 1.12
  script.cameraEntity = cameraEntity || null
  script.pointPixels = clamp(0.92 * (1.05 - getPointCountFactor(entity) * 0.34), 0.58, 0.92)
  script.pointHoldRatio = 0.4
  script.restoreEndRatio = 0.96
  script.restoreFeather = 0.04
  script.opacityBoost = 1.35
  script.endRadius = Math.max(coverageRadius, radius, 0.001) * 1.12
  script.bandWidth = 0
  script.liftAmount = 0
  script.waveTint.set(0, 0, 0)
  script.totalDuration = DURATION_MS / 1000
  script.manualEffectTimeEnabled = true
  script.manualEffectTime = 0
  script.updateEffect(0, 0)
  return script
}

function waitForRenderedFrame(app, registerCancel, timeoutMs = 500) {
  return new Promise(resolve => {
    if (!app?.on || !app?.off) {
      resolve(false)
      return
    }
    let settled = false
    let timeoutId = 0
    const finish = rendered => {
      if (settled) return
      settled = true
      window.clearTimeout(timeoutId)
      app.off('postrender', onPostrender)
      registerCancel?.(null)
      resolve(rendered)
    }
    const onPostrender = () => finish(true)
    registerCancel?.(() => finish(false))
    app.on('postrender', onPostrender)
    timeoutId = window.setTimeout(() => finish(false), timeoutMs)
  })
}

function waitForDelay(registerCancel, delayMs) {
  return new Promise(resolve => {
    let settled = false
    let timeoutId = 0
    const finish = () => {
      if (settled) return
      settled = true
      window.clearTimeout(timeoutId)
      registerCancel?.(null)
      resolve()
    }
    registerCancel?.(finish)
    timeoutId = window.setTimeout(finish, delayMs)
  })
}

function captureHandoffFrame(app, root) {
  const source = app?.graphicsDevice?.canvas
  if (!source || !root || source.width <= 0 || source.height <= 0) return null
  try {
    const snapshot = document.createElement('canvas')
    snapshot.width = source.width
    snapshot.height = source.height
    snapshot.className = 'viewer__entrance-handoff'
    snapshot.setAttribute('aria-hidden', 'true')
    const context = snapshot.getContext('2d', { alpha: false })
    if (!context) return null
    context.drawImage(source, 0, 0, snapshot.width, snapshot.height)
    root.appendChild(snapshot)
    return snapshot
  } catch {
    return null
  }
}

/**
 * Owns the one-shot camera flight and reveal effect. The callback keeps the
 * loading screen present until the shader has rendered its first point-cloud
 * frame, preventing the unstyled model from flashing before the entrance.
 */
export class ModelEntrance {
  constructor({ app, root, camera, controls }) {
    this.app = app
    this.root = root
    this.camera = camera
    this.controls = controls
    this.frame = 0
    this.runToken = 0
    this.effect = null
    this.entity = null
    this.previousControlsEnabled = true
    this.locked = false
    this.resolve = null
    this.warmupCancel = null
    this.handoffFrame = null
  }

  get playing() {
    return Boolean(this.frame || this.effect || this.root?.classList.contains('entrance-playing'))
  }

  setLocked(locked) {
    const next = Boolean(locked)
    this.root?.classList.toggle('entrance-playing', next)
    if (!this.controls) {
      this.locked = next
      return
    }
    if (next && !this.locked) {
      this.previousControlsEnabled = this.controls.enabled
      this.controls.enabled = false
      this.locked = true
    } else if (!next && this.locked) {
      this.controls.enabled = this.previousControlsEnabled
      this.locked = false
    }
  }

  removeEffect() {
    if (!this.effect) return
    this.effect.enabled = false
    this.effect = null
  }

  removeHandoffFrame() {
    this.handoffFrame?.remove()
    this.handoffFrame = null
  }

  cancel() {
    this.runToken += 1
    this.warmupCancel?.()
    this.warmupCancel = null
    if (this.frame) cancelAnimationFrame(this.frame)
    this.frame = 0
    this.removeHandoffFrame()
    this.removeEffect()
    this.entity = null
    this.setLocked(false)
    const resolve = this.resolve
    this.resolve = null
    resolve?.(false)
  }

  async play({ entity, bounds, pose, onReady }) {
    this.cancel()
    if (!entity || !bounds || !pose?.focus || !pose?.position || !this.controls) return false
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      onReady?.()
      return false
    }

    const focus = pose.focus.clone()
    const finalPosition = pose.position.clone()
    const direction = finalPosition.clone().sub(focus)
    const finalDistance = direction.length()
    if (!Number.isFinite(finalDistance) || finalDistance <= 1e-5) {
      onReady?.()
      return false
    }
    direction.mulScalar(1 / finalDistance)
    const boundsRadius = Number(bounds.halfExtents?.length?.()) || finalDistance
    const extraDistance = Math.min(
      Math.max(finalDistance * 0.2, boundsRadius * 0.12),
      finalDistance * 0.35
    )
    const startDistance = finalDistance + extraDistance
    const startPosition = applyOrbitMotion(
      focus,
      direction.clone().mulScalar(startDistance),
      INTRO_ORBIT_ANGLE
    )
    const token = ++this.runToken
    this.entity = entity
    this.effect = createIntroReveal(entity, bounds, focus, this.camera)
    this.setLocked(true)
    // The intro deliberately starts just outside the saved camera window.
    // Keep movement bounds active for user input, but do not crop the scripted
    // camera path before it reaches the final constrained pose.
    this.controls.setPose(focus, startPosition, { constrain: false })
    await waitForRenderedFrame(this.app, cancel => {
      this.warmupCancel = cancel
    })
    if (token !== this.runToken || entity !== this.entity) return false
    onReady?.()

    return new Promise(resolve => {
      this.resolve = resolve
      let startedAt = null
      let finishing = false
      const finish = async () => {
        if (finishing) return
        finishing = true
        if (token !== this.runToken) return
        this.frame = 0
        this.controls.setPose(focus, finalPosition, { constrain: false })
        if (this.effect) this.effect.manualEffectTime = 0.999 * DURATION_MS / 1000

        const finalFrame = waitForRenderedFrame(this.app, cancel => {
          this.warmupCancel = cancel
        })
        if (this.app) this.app.renderNextFrame = true
        await finalFrame
        if (token !== this.runToken || entity !== this.entity) return

        this.handoffFrame = captureHandoffFrame(this.app, this.root)
        this.removeEffect()
        for (let index = 0; index < 2; index += 1) {
          const normalFrame = waitForRenderedFrame(this.app, cancel => {
            this.warmupCancel = cancel
          })
          if (this.app) this.app.renderNextFrame = true
          await normalFrame
          if (token !== this.runToken || entity !== this.entity) return
        }

        await waitForDelay(cancel => {
          this.warmupCancel = cancel
        }, HANDOFF_SETTLE_MS)
        if (token !== this.runToken || entity !== this.entity) return
        this.handoffFrame?.classList.add('is-leaving')
        await waitForDelay(cancel => {
          this.warmupCancel = cancel
        }, HANDOFF_FADE_MS)
        if (token !== this.runToken || entity !== this.entity) return

        this.removeHandoffFrame()
        this.entity = null
        this.setLocked(false)
        this.resolve = null
        resolve(true)
      }
      const animate = timestamp => {
        if (token !== this.runToken || entity !== this.entity) return
        if (startedAt === null) startedAt = timestamp
        const progress = clamp((timestamp - startedAt) / DURATION_MS, 0, 1)
        if (this.effect) {
          this.effect.manualEffectTime = Math.min(progress, 0.999) * DURATION_MS / 1000
        }
        const cameraProgress = clamp((timestamp - startedAt) / CAMERA_DURATION_MS, 0, 1)
        const eased = cameraProgress < 0.5
          ? 4 * cameraProgress * cameraProgress * cameraProgress
          : 1 - Math.pow(-2 * cameraProgress + 2, 3) / 2
        const distance = startDistance + (finalDistance - startDistance) * eased
        const position = applyOrbitMotion(
          focus,
          direction.clone().mulScalar(distance),
          INTRO_ORBIT_ANGLE * (1 - eased)
        )
        this.controls.setPose(focus, position, { constrain: false })
        if (progress >= 1) {
          void finish()
        } else {
          this.frame = requestAnimationFrame(animate)
        }
      }
      this.frame = requestAnimationFrame(animate)
    })
  }

  destroy() {
    this.cancel()
    this.removeHandoffFrame()
    this.app = null
    this.root = null
    this.camera = null
    this.controls = null
  }
}
